import React, { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, ButtonGroup, Button, Grid, Card } from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import { toLocal } from "../../../utils/date";
import { getSafetyInsightApi, getSafetyReportsApi, getSafetyDetailsApi, getSafetyManhoursApi } from "../../../api/safety";
import { getSafetyObservationApi } from "../../../api/comment";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import { useUser } from "../../../hook/useUser.js";
import dayjs from "dayjs";
import MainTable from "../../../component/table/MainTable.js";
import SelectComponent from "../../../component/SelectComponent.js";
import TooltipInfo from "../../../component/TooltipInfo.js";
import ManageSafetyPopup from "./ManageSafetyPopup.js";
import FullScreenLoading from "../../../component/FullScreenLoading.js";

const SummaryCard = ({ title, count, change, color }) => (
  <Card
    sx={{
      flex: 1,
      backgroundColor: color,
      borderRadius: "10px",
      color: "#fff",
      py: 0.5,
      px: 1,
      textAlign: "center",
      height: "100%",
    }}
  >
    <Typography component="span" sx={{ fontWeight: 600, fontSize: 28 }}>
      {count}
    </Typography>
    {change !== undefined && (
      <Typography variant="subtitle1" component="span">
        （{change >= 0 && "+"}
        {change}）
      </Typography>
    )}
    <Typography sx={{ fontWeight: 600, fontSize: 12 }}>{title}</Typography>
  </Card>
);

const KPITooltip = () => {
  const icon = (
    <HelpOutline
      sx={{
        position: "absolute",
        top: "10px",
        right: "15px",
        color: "#717273",
      }}
    />
  );
  const content = (
    <Grid container spacing={2} sx={{ px: 2, py: 1.5 }}>
      <Grid item xs={12}>
        Please note: For all project KPIs with parenthesis next to it.
      </Grid>
      <Grid item xs={6}>
        <SummaryCard title={"Pending Site Matter Observation"} count={"XXX"} change={"+XX"} color={"#A459D1"} />
      </Grid>
      <Grid item xs={6}>
        <Box sx={{ p: 1, border: "1px solid #D0D5DE" }}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <img src="assets/illustrations/manhours.svg" alt="incidents" />
            <Typography variant="h5" gutterBottom>
              XXX
            </Typography>
            <Typography variant="body1" gutterBottom>
              &nbsp;&nbsp;(+XX)
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ textAlign: "center", whiteSpace: "nowrap" }}>
            Manhours Count
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        The first number represents the KPI's accumulated value since the project began. The value in parentheses indicates the accumulated record
        starting from Monday at 12:01 AM of the current week.
      </Grid>
    </Grid>
  );
  return <TooltipInfo icon={icon} content={content} />;
};

const SafetyInsight = ({ showButton = false }) => {
  const { projectId } = useParams();
  const { user } = useUser(projectId);
  const { showError } = useContext(NotificationContext);
  const [currentSafetyTab, setSafetyCurrentTab] = useState(0);
  const [currentDateTab, setSafetyDateTab] = useState(0);
  const [editPopup, setEditPopup] = useState(false);
  const [readOnlyPopup, setReadOnlyPopup] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const [partyValue, setPartyValue] = useState([]);
  const [safetyReportData, setSafetyReportData] = useState(null);
  const [SIFPData, setSIFPData] = useState(null);
  const [SIFAData, setSIFAData] = useState(null);
  const [observationsData, setObservationsData] = useState(null);
  const [observationsHead, setObservationsHead] = useState([
    { id: "damagingEnergy", label: "Damaging Energy", align: "left", defaultSortOrder: "asc" },
    { id: "week", label: "Week 11 19-23/2/2024", align: "left" },
    { id: "month", label: "Month 3 Feb 2024", align: "left" },
    { id: "projectToDate", label: "Project to Date", align: "left" },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPopup, setIsLoadingPopup] = useState(false);
  const [manhoursData, setManhoursData] = useState({});
  const [filteredManhoursData, setFilteredManhoursData] = useState([]);

  const dateTab = [
    { label: "Project to Date", key: "projectToDate" },
    { label: "Current Month", key: "currentMonth" },
    { label: "Current Week", key: "currentWeek" },
  ];
  const SIFPTableHead = [
    { id: "sifpId", label: "SIFP ID", align: "left" },
    { id: "datetime", label: "Date Time", align: "left", type: "dateWithTime" },
    // { id: "location", label: "Location", align: "left" },
    { id: "subject", label: "Subject", align: "left" },
    { id: "alertNo", label: "Safety alert no.", align: "left" },
    { id: "reporter", label: "Reporter", align: "left" },
  ];
  const SIFATableHead = [
    { id: "sifpId", label: "SIFA ID", align: "left" },
    { id: "datetime", label: "Date Time", align: "left", type: "dateWithTime" },
    // { id: "location", label: "Location", align: "left" },
    { id: "subject", label: "Subject", align: "left" },
    { id: "alertNo", label: "Safety alert no.", align: "left" },
    { id: "reporter", label: "Reporter", align: "left" },
  ];
  const manhoursTableHead = [
    { id: "party", label: "Party", align: "left" },
    { id: "category", label: "Category", align: "left" },
    { id: "manhour", label: "Manhour", align: "left" },
  ];
  const [safetyTab, setSafetyTab] = useState([
    {
      name: "sifPotential",
      label: "SIF Potential",
      value: 0,
      change: 0,
      icon: "assets/illustrations/minor.svg",
    },
    {
      name: "sifActual",
      label: "SIF Actual",
      value: 0,
      change: 0,
      icon: "assets/illustrations/significant.svg",
    },
    {
      name: "manhour",
      label: "Manhours Count",
      value: 0,
      change: 0,
      icon: "assets/illustrations/manhours.svg",
    },
    {
      name: "safetyObservations",
      label: "Safety Reporting & Observation",
      value: 0,
      change: 0,
      icon: "assets/illustrations/reporting.svg",
    },
  ]);

  const handleRowSelect = async (row) => {
    setIsLoadingPopup(true);
    try {
      const result = await getSafetyDetailsApi(projectId, row[0]);
      const data = result?.data[0];
      if (data) {
        setReadOnlyPopup(true);
        setSafetyReportData({
          ...data,
          date: toLocal(data?.datetime),
          time: toLocal(data?.datetime).format("HH:mm"),
          subject: data?.subject || "-",
          classificationList: [
            { value: 1, label: "SIF Potential" },
            { value: 2, label: "SIF Actual" },
          ],
        });
      }
    } catch {
      showError("Received report data occurred error.");
    } finally {
      setIsLoadingPopup(false);
    }
  };

  const getReportsData = async () => {
    setIsLoading(true);
    try {
      const potential = await getSafetyReportsApi(projectId, 1);
      const actual = await getSafetyReportsApi(projectId, 2);
      if (potential?.data) {
        const formattedData = potential.data.map((item) => {
          return {
            ...item,
            id: item.safetyReportId,
          };
        });
        setSIFPData(formattedData);
      }
      if (actual?.data) {
        const formattedData = actual.data.map((item) => {
          return {
            ...item,
            id: item.safetyReportId,
          };
        });
        setSIFAData(formattedData);
      }
    } catch {
      showError("Received report data occurred error.");
    } finally {
      setIsLoading(false);
    }
  };

  const getInsightData = async () => {
    try {
      const result = await getSafetyInsightApi(projectId);
      if (result?.data) {
        const updatedTab = safetyTab.map((tabItem) => {
          const apiData = result.data[tabItem.name];
          if (apiData) {
            return {
              ...tabItem,
              value: apiData.all,
              change: apiData.currentWeek,
            };
          }
          return tabItem;
        });
        setSafetyTab(updatedTab);
      }
    } catch {
      showError("Received insight data occurred error.");
    }
  };

  const getManhoursData = async () => {
    setIsLoading(true);
    try {
      const result = await getSafetyManhoursApi(projectId);
      if (result?.data) {
        const { projectToDate, currentMonth, currentWeek } = result.data;

        const allParties = [
          ...projectToDate.map((item) => item.party),
          ...currentMonth.map((item) => item.party),
          ...currentWeek.map((item) => item.party),
        ];

        const uniqueParties = [...new Set(allParties)];
        const options = uniqueParties?.filter((party) => party)?.map((party) => ({ value: party, label: party }));
        setManhoursData(result.data);
        setPartyOptions(options);

        if (user?.userRole === "Contractor") {
          setPartyValue([user?.company]);
        } else {
          setPartyValue(uniqueParties);
        }
      }
    } catch {
      showError("Received manhours data occurred error.");
    } finally {
      setIsLoading(false);
    }
  };

  const getObservationData = async () => {
    setIsLoading(true);
    try {
      const result = await getSafetyObservationApi(projectId);
      if (result) {
        const { weekNo, weekStart, weekEnd, monthNo, monthStart, observations } = result;
        const updatedHead = [
          { id: "damagingEnergy", label: "Damaging Energy", align: "left", defaultSortOrder: "asc" },
          {
            id: "week",
            label: `Week ${weekNo} ${dayjs(weekStart).format("DD/M")}-${dayjs(weekEnd).format("DD/M/YYYY")}`,
            align: "left",
          },
          {
            id: "month",
            label: `Month ${monthNo} ${dayjs(monthStart).format("MMM")} ${dayjs(monthStart).year()}`,
            align: "left",
          },
          { id: "projectToDate", label: "Project to Date", align: "left" },
        ];
        setObservationsHead(updatedHead);
        setObservationsData(observations);
      }
    } catch {
      showError("Received observations data occurred error.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const filterData = (data) => {
      if (user?.userRole === "Contractor") {
        return data?.filter((item) => item.party === user?.company);
      }
      if (!partyValue.length) return data;
      return data?.filter((item) => partyValue.includes(item.party));
    };

    const updateFilteredData = () => {
      const key = dateTab[currentDateTab]?.key;
      const filteredData = {
        projectToDate: filterData(manhoursData.projectToDate),
        currentMonth: filterData(manhoursData.currentMonth),
        currentWeek: filterData(manhoursData.currentWeek),
      };

      setFilteredManhoursData(filteredData[key] || []);
    };

    updateFilteredData();
  }, [currentDateTab, partyValue, manhoursData, user]);

  useEffect(() => {
    getInsightData();
    getReportsData();
    getObservationData();
    getManhoursData();
  }, []);

  return (
    <>
      {showButton && (
        <Box>
          <Button
            onClick={() => setEditPopup(true)}
            variant="contained"
            color="secondary"
            sx={{
              pr: 1.5,
              pl: 1,
              py: 0,
              lineHeight: "30px",
              borderRadius: "20px",
              position: "absolute",
              right: "40px",
              top: "5px",
            }}
            startIcon={<img src="assets/buttons/set.svg" alt="Manage Safety" style={{ height: 20 }} />}
          >
            Manage Safety
          </Button>
          <KPITooltip />
        </Box>
      )}
      <Box
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 25px)",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          {safetyTab.map((tab, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                cursor: "pointer",
                width: "25%",
                pt: 0.5,
                minHeight: 72,
                border: "1px solid #D0D5DE",
                borderLeft: index === 1 || index === 2 || index === 3 ? "none" : "1px solid #D0D5DE",
                borderBottom: currentSafetyTab === index ? "6px solid #33A3DB" : "1px solid #D0D5DE",
              }}
              onClick={() => setSafetyCurrentTab(index)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <img src={tab.icon} alt="Safety insight" style={{ height: 28 }} />
                <Box>
                  <Typography variant="span" style={{ fontSize: 20 }}>
                    {tab.value}
                  </Typography>
                  <Typography variant="span">
                    （{tab.change >= 0 && "+"}
                    {tab.change}）
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ textAlign: "center", fontSize: "12px" }}>{tab.label}</Typography>
              </Box>
            </Box>
          ))}
        </Box>
        {currentSafetyTab === 0 && (
          <MainTable
            paperSx={{ height: "100%" }}
            sx={{ height: "100%", maxHeight: "100%" }}
            columns={SIFPTableHead}
            rows={SIFPData}
            headerStyles={{
              default: { backgroundColor: "#F1F1F1" },
            }}
            emptyText="No SIF Potential Report Available"
            selectable="single"
            setSelected={handleRowSelect}
            isLoading={isLoading}
          />
        )}
        {currentSafetyTab === 1 && (
          <MainTable
            paperSx={{ height: "100%" }}
            sx={{ height: "100%", maxHeight: "100%" }}
            columns={SIFATableHead}
            rows={SIFAData}
            headerStyles={{
              default: { backgroundColor: "#F1F1F1" },
            }}
            emptyText="No SIF Actual Report Available"
            selectable="single"
            setSelected={handleRowSelect}
            isLoading={isLoading}
          />
        )}
        {currentSafetyTab === 2 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "calc(100% - 88px)",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                minWidth: 265,
              }}
            >
              <ButtonGroup variant="text" color="primary">
                {dateTab.map((tab, index) => {
                  return (
                    <Button
                      key={index}
                      onClick={() => setSafetyDateTab(index)}
                      style={{
                        color: "#717273",
                        border: "1px solid #D0D5DE",
                        height: "32px",
                        borderBottom: currentDateTab === index ? "3px solid #33A3DB" : "1px solid #D0D5DE",
                        borderLeft: index === 0 ? "1px solid #D0D5DE" : "none",
                      }}
                    >
                      {tab.label}
                    </Button>
                  );
                })}
              </ButtonGroup>
              {user?.userRole !== "Contractor" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <SelectComponent
                    options={partyOptions}
                    onChange={(value) => setPartyValue(value)}
                    value={partyValue}
                    isMultiSelect
                    selectLabel="Party"
                  />
                </Box>
              )}
            </Box>
            <MainTable
              paperSx={{ height: "100%" }}
              sx={{ height: "100%", maxHeight: "100%" }}
              columns={manhoursTableHead}
              rows={filteredManhoursData}
              headerStyles={{
                default: { backgroundColor: "#F1F1F1" },
              }}
              isLoading={isLoading}
            />
          </Box>
        )}
        {currentSafetyTab === 3 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "calc(100% - 88px)",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: "11px" }}>
              For further detail please go to Observation Page, Filter by Safety and Damaging Energies/Positive Safety Observations.
            </Typography>
            <MainTable
              paperSx={{ height: "100%" }}
              sx={{ height: "100%", maxHeight: "100%" }}
              columns={observationsHead}
              rows={observationsData}
              headerStyles={{
                default: { backgroundColor: "#F1F1F1" },
              }}
              sortable
              sortableColumns={["damagingEnergy"]}
              isLoading={isLoading}
            />
          </Box>
        )}
      </Box>
      <ManageSafetyPopup
        open={editPopup}
        onClose={() => setEditPopup(false)}
        onAdded={() => {
          getReportsData();
        }}
      />
      <ManageSafetyPopup open={readOnlyPopup} onClose={() => setReadOnlyPopup(false)} data={safetyReportData} readOnly />
      <FullScreenLoading isLoading={isLoadingPopup} />
    </>
  );
};
export default SafetyInsight;
